import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const DetailItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-4 p-4 rounded-lg border border-border bg-background/50">
    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size={18} color="var(--color-primary)" />
    </div>

    <div className="min-w-0">
      <p className="text-sm text-muted-foreground font-caption">{label}</p>
      <p className="text-base font-medium text-foreground font-body break-words mt-1">
        {value || '-'}
      </p>
    </div>
  </div>
);

const TextBlock = ({ title, value }) => (
  <div className="bg-card border border-border rounded-lg p-5">
    <h3 className="text-lg font-semibold text-foreground font-heading mb-3">
      {title}
    </h3>
    <div className="text-sm text-foreground font-body whitespace-pre-line leading-7">
      {value || '-'}
    </div>
  </div>
);

const ClientProfileDetails = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location?.search);
  const clientId = searchParams?.get('id');

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const response = await fetch(`http://localhost:5000/api/business/${clientId}`);
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || 'Failed to fetch client details');
        }

        setClientData(result?.data || null);
      } catch (error) {
        console.error('Fetch client details error:', error);
        setClientData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchClientDetails();
  }, [clientId]);

  const formatted = useMemo(() => {
    if (!clientData) return null;

    return {
      id: clientData?._id,
      fullName: clientData?.fullName || '-',
      mobileNumber: clientData?.mobileNumber || '-',
      email: clientData?.email || '-',
      companyName: clientData?.companyName || '-',
      website: clientData?.website || '-',
      industry: clientData?.industry || '-',
      businessType: clientData?.businessType
        ? clientData.businessType.charAt(0).toUpperCase() + clientData.businessType.slice(1)
        : '-',
      revenueRange: clientData?.revenueRange || '-',
      primaryGoal: clientData?.primaryGoal || '-',
      createdDate: clientData?.createdAt
        ? new Date(clientData.createdAt).toLocaleDateString('en-CA')
        : '-',
      lastActiveDate: clientData?.lastActiveDate
        ? new Date(clientData.lastActiveDate).toLocaleDateString('en-CA')
        : '-',
      status: clientData?.status || 'active',

      problemSolved: clientData?.problemSolved || '-',
      biggestChallenge: clientData?.biggestChallenge || '-',
      keyPartners: clientData?.keyPartners || '-',
      valueProposition: clientData?.valueProposition || '-',
      customerRelationships: clientData?.customerRelationships || '-',
      customerSegments: clientData?.customerSegments || '-',
      keyActivities: clientData?.keyActivities || '-',
      channels: clientData?.channels || '-',
      keyResources: clientData?.keyResources || '-',
      costStructure: clientData?.costStructure || '-',
      revenueStreams: clientData?.revenueStreams || '-',
    };
  }, [clientData]);

  const tabs = [
    { id: 'company', label: 'Company Details', icon: 'Building2' },
    { id: 'bmc', label: 'Business Model Canvas', icon: 'LayoutGrid' }
  ];

  const handleBack = () => {
    navigate('/super-admin/clients');
  };

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden">
      <AdminSidebar isCollapsed={isSidebarCollapsed} />

      <div
        className={`flex-1 min-w-0 flex flex-col overflow-hidden transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <AuthHeader
          isAuthenticated={true}
          userName="Super Admin"
          userSubscription="admin"
          onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-6 pt-20 md:pt-24">
            <div className="flex items-start">
              <Button
                variant="ghost"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={handleBack}
              >
                Back to All Clients
              </Button>
            </div>

            {loading ? (
              <div className="bg-card border border-border rounded-lg p-10 text-center">
                <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">Loading client details...</p>
              </div>
            ) : !formatted ? (
              <div className="bg-card border border-border rounded-lg p-10 text-center">
                <Icon name="AlertCircle" size={40} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-semibold text-foreground mb-2">Client not found</p>
                <p className="text-sm text-muted-foreground mb-4">
                  The selected client details could not be loaded.
                </p>
                <Button onClick={handleBack}>Back</Button>
              </div>
            ) : (
              <>
                <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Building" size={28} color="var(--color-primary)" />
                      </div>

                      <div className="min-w-0">
                        <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading break-words">
                          {formatted.companyName}
                        </h1>

                        <p className="text-sm text-muted-foreground font-caption mt-1 break-all">
                          {formatted.fullName} • {formatted.email}
                        </p>

                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-caption ${
                              formatted.status === 'active'
                                ? 'bg-success/10 text-success'
                                : 'bg-destructive/10 text-destructive'
                            }`}
                          >
                            {formatted.status === 'active' ? 'Active' : 'Inactive'}
                          </span>

                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-caption bg-primary/10 text-primary">
                            {formatted.businessType}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground font-caption">Created Date</p>
                        <p className="text-xl font-bold text-foreground font-heading mt-1">
                          {formatted.createdDate}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="CalendarDays" size={20} color="var(--color-primary)" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground font-caption">Last Date</p>
                        <p className="text-xl font-bold text-foreground font-heading mt-1">
                          {formatted.lastActiveDate}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                        <Icon name="Clock3" size={20} color="var(--color-success)" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground font-caption">Business Type</p>
                        <p className="text-xl font-bold text-foreground font-heading mt-1">
                          {formatted.businessType}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-violet/10 flex items-center justify-center">
                        <Icon name="Briefcase" size={20} color="var(--color-primary)" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg elevation-sm overflow-hidden">
                  <div className="flex gap-1 px-4 pt-4 border-b border-border overflow-x-auto">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-t-md text-sm font-medium transition-smooth whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted/50'
                        }`}
                      >
                        <Icon name={tab.icon} size={16} />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="p-4 md:p-6">
                    {activeTab === 'company' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DetailItem icon="User" label="Full Name" value={formatted.fullName} />
                        <DetailItem icon="Phone" label="Mobile Number" value={formatted.mobileNumber} />
                        <DetailItem icon="Mail" label="Email" value={formatted.email} />
                        <DetailItem icon="Building2" label="Company Name" value={formatted.companyName} />
                        <DetailItem icon="Globe" label="Website" value={formatted.website} />
                        <DetailItem icon="Briefcase" label="Industry" value={formatted.industry} />
                        <DetailItem icon="Tag" label="Business Type" value={formatted.businessType} />
                        <DetailItem icon="IndianRupee" label="Revenue Range" value={formatted.revenueRange} />
                        <DetailItem icon="Target" label="Primary Goal" value={formatted.primaryGoal} />
                        <DetailItem icon="CalendarDays" label="Created Date" value={formatted.createdDate} />
                        <DetailItem icon="Clock3" label="Last Date" value={formatted.lastActiveDate} />
                        <DetailItem
                          icon="ShieldCheck"
                          label="Status"
                          value={formatted.status === 'active' ? 'Active' : 'Inactive'}
                        />
                      </div>
                    )}

                    {activeTab === 'bmc' && (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <TextBlock title="What problem does your business solve?" value={formatted.problemSolved} />
                        <TextBlock title="What is your biggest challenge right now?" value={formatted.biggestChallenge} />
                        <TextBlock title="Key Partners" value={formatted.keyPartners} />
                        <TextBlock title="Value Proposition" value={formatted.valueProposition} />
                        <TextBlock title="Customer Relationships" value={formatted.customerRelationships} />
                        <TextBlock title="Customer Segments" value={formatted.customerSegments} />
                        <TextBlock title="Key Activities" value={formatted.keyActivities} />
                        <TextBlock title="Channels" value={formatted.channels} />
                        <TextBlock title="Key Resources" value={formatted.keyResources} />
                        <TextBlock title="Cost Structure" value={formatted.costStructure} />
                        <TextBlock title="Revenue Streams" value={formatted.revenueStreams} />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientProfileDetails;